import { Button, Divider, Flex, Input, LoadingOverlay, Modal } from "@mantine/core";
import { IconEdit, IconSearch, IconTrash } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { sectionApi } from "../../apis";
import { ISection } from "../../interfaces/ISection";
import { Dialog } from "../../utils/confirm-modal";
import { dateTimeFormat } from "../../utils/date";
import HttpHandler from "../../utils/http-handler";
import { Message } from "../../utils/message";
import { PageLayout } from "../layout";
import { ColumnType, ITableRef, Table } from "../table";
import { Tag } from "../tags";
import RenderText from "../ui/render-text";
import { SectionForm } from "./section-form";

type ISectionData = {
  id: string;
  name: string;
  isPublic: boolean;
  isDehub: boolean;
  createdAt: string;
};

export function SectionList() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ query: "" });
  const [action, setAction] = useState<string[]>([]);
  const [payload, setPayload] = useState<ISectionData | null>();
  const ref = useRef<ITableRef>(null);
  const [loading, setLoading] = useState(false);

  const columns = useHeader({
    onClick: (key, item) => {
      switch (key) {
        case "edit": {
          setPayload(item);
          setAction(["update", item as any]);
          break;
        }

        case "remove": {
          Dialog.confirm("Та үүнийг устгахдаа итгэлтэй байна уу?", async (key) => {
            switch (key) {
              case "confirm": {
                setLoading(true);
                try {
                  await sectionApi.remove(item.id);
                  ref.current?.reload();
                  Message.success("Амжилттай брэндийг устгалаа!");
                  setLoading(false);
                } catch (err) {
                  setLoading(false);
                  Message.error((err as HttpHandler)?.message!);
                }
                break;
              }

              default:
            }
          });
          break;
        }
      }
    },
  });

  const onCancel = async () => {
    setAction([]);
    setPayload(null);
  };

  const reload = () => {
    ref.current?.reload();
  };

  if (loading) return <LoadingOverlay visible={loading} />;

  return (
    <>
      <PageLayout
        title="Барааны нэмэлт мэдээлэл"
        subTitle="Барааны нэмэлт мэдээлэлийн жагсаалт, шинээр үүсгэх, засварлах"
        extra={[
          <Input leftSection={<IconSearch />} placeholder="Нэрээр хайх" onChange={(e) => setFilters({ query: e.currentTarget.value })} />,
          <Button variant="default" onClick={() => navigate("/reference")}>
            Буцах
          </Button>,
          <Button
            onClick={() => {
              setAction(["new"]);
            }}>
            Нэмэх
          </Button>,
        ]}>
        <Divider my={10} />
        <Table ref={ref} name="section.list" columns={columns} filters={filters} loadData={(data) => sectionApi.list(data!)} />
      </PageLayout>
      <Modal opened={!!action[0]} onClose={() => setAction([])} withCloseButton={false} size="80%" centered>
        <SectionForm onCancel={onCancel} action={action} payload={payload || undefined} reload={reload} />
      </Modal>
    </>
  );
}

const useHeader = ({ onClick }: { onClick: (key: string, record: ISection) => void }): ColumnType<ISection>[] => [
  {
    title: "#",
    width: "30px",
    render: (_r, index) => <RenderText text={index + 1} />,
  },
  {
    title: "Үйлдэл",
    render: (record) => {
      return (
        <Flex gap="sm">
          <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("edit", record)}>
            <IconEdit />
          </Button>
          <Button variant="light" color="red" radius={100} w={35} h={35} p={0} onClick={() => onClick("remove", record)}>
            <IconTrash />
          </Button>
        </Flex>
      );
    },
  },
  {
    title: "Нэр",
    dataIndex: "name",
    render: (record) => <RenderText text={record.name ?? "-"} />,
  },
  {
    title: "Нээлттэй эсэх",
    dataIndex: "isPublic",
    render: (record) => <Tag name={record.isPublic ? "Тийм" : "Үгүй"} color={record.isPublic ? "green.5" : "red.5"} />,
  },
  {
    title: "DeHub эсэх",
    dataIndex: "isDehub",
    render: (record) => <Tag name={record.isDehub ? "Тийм" : "Үгүй"} color={record.isDehub ? "green.5" : "red.5"} />,
  },
  {
    title: "Бүртгэсэн огноо",
    dataIndex: "createdAt",
    render: (record) => <RenderText text={dateTimeFormat(record.createdAt)} />,
  },
];
